import React, { createContext, useContext, useState, useEffect } from 'react';
import { ConfigProvider, Empty } from 'antd';
import ruRU from 'antd/lib/locale/ru_RU';
import kkKZ from 'antd/lib/locale/kk_KZ';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import 'dayjs/locale/kk';

import ru from './locales/ru';
import kz from './locales/kz';

const translations = {
  ru,
  kz,
  kk: kz,
};

export const I18nContext = createContext({
  lang: 'ru',
  setLang: () => {},
  t: (key, fallback) => fallback || key,
});

export const getSavedLanguage = () => {
  try {
    const saved = localStorage.getItem('lang');
    if (saved === 'kz' || saved === 'kk' || saved === 'ru') {
      return saved === 'kk' ? 'kz' : saved;
    }
  } catch (e) {
    // ignore
  }
  return 'ru';
};

export const t = (key, fallback = '') => {
  const currentLang = getSavedLanguage();
  const dict = translations[currentLang] || translations.ru;
  if (dict && dict[key] !== undefined) {
    return dict[key];
  }
  if (currentLang === 'kz' && translations.kz && translations.kz[key] !== undefined) {
    return translations.kz[key];
  }
  if (translations.ru && translations.ru[key] !== undefined) {
    return translations.ru[key];
  }
  return fallback || key;
};

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(getSavedLanguage());

  const setLang = (newLang) => {
    const normalized = newLang === 'kk' ? 'kz' : newLang;
    try {
      localStorage.setItem('lang', normalized);
    } catch (e) {}
    setLangState(normalized);
    try {
      dayjs.locale(normalized === 'kz' ? 'kk' : 'ru');
    } catch (e) {}
    // Dispatch custom event for non-react or global listeners
    try {
      window.dispatchEvent(new Event('languageChange'));
    } catch (e) {}
  };

  useEffect(() => {
    try {
      dayjs.locale(lang === 'kz' ? 'kk' : 'ru');
    } catch (e) {}
    const handleStorage = () => {
      const current = getSavedLanguage();
      if (current !== lang) {
        setLangState(current);
      }
    };
    window.addEventListener('languageChange', handleStorage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('languageChange', handleStorage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [lang]);

  const translate = (key, fallback = '') => {
    const dict = translations[lang] || translations.ru;
    if (dict && dict[key] !== undefined) {
      return dict[key];
    }
    if (lang === 'kz' && translations.kz && translations.kz[key] !== undefined) {
      return translations.kz[key];
    }
    if (translations.ru && translations.ru[key] !== undefined) {
      return translations.ru[key];
    }
    return fallback || key;
  };

  const rawLocale = lang === 'kz' ? kkKZ : ruRU;
  const antdLocale = rawLocale && rawLocale.default ? rawLocale.default : rawLocale;

  const renderCustomEmpty = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
          {translate('common.empty', lang === 'kz' ? 'Деректер жоқ' : 'Нет данных')}
        </span>
      }
    />
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, locale: lang, setLocale: setLang, t: translate }}>
      <ConfigProvider locale={antdLocale} renderEmpty={renderCustomEmpty}>
        {children}
      </ConfigProvider>
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  const currentLang = ctx?.lang || getSavedLanguage() || 'ru';
  return {
    ...ctx,
    lang: currentLang,
    locale: currentLang,
    setLang: ctx?.setLang || (() => {}),
    setLocale: ctx?.setLang || (() => {}),
    t: ctx?.t || t,
  };
};

export default {
  t,
  I18nProvider,
  useI18n,
  I18nContext,
};

